/**
 * Standalone wishlist Web Component for Shopify theme.
 * Integrates with Swym wishlist API (_swat global).
 * Usage:
 *   <wishlist-button data-product-id="123" data-variant-id="456" data-product-url="/products/handle?variant=456"></wishlist-button>
 */

function getSwat() {
  return new Promise((resolve) => {
    if (window._swat) return resolve(window._swat);
    if (!window.SwymCallbacks) window.SwymCallbacks = [];
    window.SwymCallbacks.push((swat) => resolve(swat));
  });
}

const PLUS_CIRCLE_SVG = `<svg class="wishlist-btn__icon" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
  <path d="M12 8V16M8 12H16M22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
</svg>`;

const CHECK_CIRCLE_SVG = `<svg class="wishlist-btn__icon" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
  <path d="M7.5 12L10.5 15L16.5 9M22 12C22 17.5228 17.5228 22 12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
</svg>`;

class WishlistButton extends HTMLElement {
  static get observedAttributes() {
    return ['data-variant-id', 'data-product-url'];
  }

  constructor() {
    super();
    this.wishlisted = false;
    this.ready = false;
  }

  connectedCallback() {
    this._boundHandleClick = this.handleClick.bind(this);
    this._loadState();
    this.addEventListener('click', this._boundHandleClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this._boundHandleClick);
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue === null || oldValue === newValue) return;
    this.wishlisted = false;
    this.ready = false;
    this._loadState();
  }

  _loadState() {
    this.productId = Number(this.dataset.productId);
    this.variantId = Number(this.dataset.variantId);
    const rawUrl = this.dataset.productUrl || '';
    this.productUrl = rawUrl.startsWith('/') ? window.location.origin + rawUrl : rawUrl;
    this.source = this.dataset.source || 'pdp';
    this.label = this.dataset.label || '';
    this.labelAdded = this.dataset.labelAdded || '';
    // Transient feedback shown next to the icon-only (card) button on click.
    this.flashAdded = this.dataset.flashAdded || '';
    this.flashRemoved = this.dataset.flashRemoved || '';

    this.render();
    this.fetchWishlistState();
  }

  fetchWishlistState() {
    getSwat().then((swat) => {
      swat.fetch((items) => {
        const found = items.some((item) => Number(item.epi) === this.variantId);
        this.wishlisted = found;
        this.ready = true;
        this.render();
      });
    });
  }

  handleClick(e) {
    e.preventDefault();
    e.stopPropagation();
    if (!this.ready) return;

    const product = {
      epi: this.variantId,
      empi: this.productId,
      du: this.productUrl,
      source: this.source,
    };

    getSwat().then((swat) => {
      if (this.wishlisted) {
        swat.removeFromWishList(product, () => {
          this.wishlisted = false;
          this.render();
          this.flash(this.flashRemoved);
          this.dispatchEvent(new CustomEvent('wishlist:changed', { bubbles: true, detail: { wishlisted: false, variantId: this.variantId } }));
        });
      } else {
        swat.addToWishList(product, () => {
          this.wishlisted = true;
          this.render();
          this.flash(this.flashAdded);
          this.dispatchEvent(new CustomEvent('wishlist:changed', { bubbles: true, detail: { wishlisted: true, variantId: this.variantId } }));
        });
      }
    });
  }

  render() {
    const ariaLabel = this.wishlisted ? 'Remove from wishlist' : 'Add to wishlist';
    const icon = this.wishlisted ? CHECK_CIRCLE_SVG : PLUS_CIRCLE_SVG;

    if (this.label) {
      const textLabel = this.wishlisted && this.labelAdded ? this.labelAdded : this.label;
      this.innerHTML = `<button type="button" class="wishlist-btn wishlist-btn--labeled" aria-label="${ariaLabel}" aria-pressed="${this.wishlisted}">${icon}<span class="wishlist-btn__label">${textLabel}</span></button>`;
    } else {
      this.innerHTML = `<button type="button" class="wishlist-btn" aria-label="${ariaLabel}" aria-pressed="${this.wishlisted}"><span class="wishlist-btn__flash" aria-hidden="true"><span class="wishlist-btn__flash-text"></span></span>${icon}</button>`;
    }

    // This async re-render would undo the carousel's tabindex=-1 on hidden slides; re-apply it.
    if (this.closest('[aria-hidden="true"]')) {
      const button = this.querySelector('.wishlist-btn');
      if (button) {
        button.setAttribute('tabindex', '-1');
        button.setAttribute('data-restored-tabindex', '');
      }
    }
  }

  // Briefly reveal a feedback word ("Favorited" / "Removed") that unfolds
  // right-to-left from the left edge of the icon, then auto-collapses.
  flash(message) {
    if (!message) return;
    const button = this.querySelector('.wishlist-btn');
    const text = this.querySelector('.wishlist-btn__flash-text');
    if (!button || !text) return;

    text.textContent = message;
    // Restart the animation if a previous flash is still showing.
    button.classList.remove('is-flashing');
    void button.offsetWidth;
    button.classList.add('is-flashing');

    clearTimeout(this._flashTimer);
    this._flashTimer = setTimeout(() => {
      button.classList.remove('is-flashing');
    }, 1600);
  }
}

if (!customElements.get('wishlist-button')) {
  customElements.define('wishlist-button', WishlistButton);
}
